import * as React from "react";
import { ClockIcon, IconComponent, ListIcon, Tab, Tabs, TabsLayout, TruckIcon } from "../../ui";
import { PartsFilter } from "../partsStatus.utils";

const options: { key: PartsFilter; label: string; Icon: IconComponent }[] = [
  { key: "ALL", label: "All", Icon: ListIcon },
  { key: "AWAITING", label: "Ordered", Icon: ClockIcon },
  { key: "COLLECTED", label: "Picked up", Icon: TruckIcon },
];

export const PartsFilterTabs: React.FC<{
  value: PartsFilter;
  onChange: (next: PartsFilter) => void;
  counts: Record<PartsFilter, number>;
}> = ({ value, onChange, counts }) => (
  /*
   * Scrollable, not fullWidth. fullWidth gives every tab exactly one third of the
   * screen — about 120px on a phone, which a label plus its icon and count can outgrow.
   * Sizing to
   * content instead lets the short tab hand its slack to the long one: the three come
   * to roughly 300px together, so they still fit a 360px screen without scrolling, and
   * on anything narrower they scroll rather than shrink or wrap.
   *
   * Each tab then flex-grows into whatever room is left over, so the row fills the
   * width evenly instead of packing to the left with a gap at the end — but with
   * flex-shrink off, so when the labels genuinely do not fit they overflow and scroll
   * rather than being squeezed.
   *
   * Sizes are in rem, not px: px ignores the browser and OS font-size setting, so a
   * technician who has scaled their phone's text up would see none of it. rem scales
   * with that setting, and because the tabs size to their content and scroll, larger
   * text simply takes more room instead of wrapping or clipping.
   */
  <Tabs value={value} onChange={onChange} layout={TabsLayout.SCROLLABLE} aria-label="Filter part orders">
    {options.map((option) => (
      <Tab
        key={option.key}
        value={option.key}
        // Icon inline with the label rather than the `icon` prop, which stacks them
        // and doubles the height of the sticky header.
        label={
          <span className="flex items-center justify-center gap-1 whitespace-nowrap">
            <option.Icon style={{ fontSize: "1rem", flexShrink: 0 }} />
            {option.label}
            {counts[option.key] ? (
              <span style={{ fontSize: "0.625rem", fontWeight: 400, opacity: 0.75 }}>({counts[option.key]})</span>
            ) : null}
          </span>
        }
        style={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          minWidth: 0,
          padding: "0.375rem 0.625rem",
          textTransform: "none",
          flexGrow: 1,
          flexShrink: 0,
          flexBasis: "auto",
        }}
      />
    ))}
  </Tabs>
);
